import { useEffect, useState } from "react";
import { Printer, ScanBarcode, RefreshCw, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../../components/PageHeader";
import {
  connectQZ,
  isQZConnected,
  findQZPrinters,
} from "../../lib/printer/qz";
import { printDocument } from "../../lib/printer";

const PRINTER_CONNECTION_QZ = "qz_desktop";
const PRINTER_CONNECTION_CLEANTER = "cleanter";
const PRINTER_CONNECTION_BROWSER = "browser";

function useLocal(key, def) {
  const [v, setV] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(key)) ?? def;
    } catch {
      return def;
    }
  });

  const save = (nv) => {
    setV(nv);
    localStorage.setItem(key, JSON.stringify(nv));
  };

  return [v, save];
}

export default function HardwarePage() {
  const [printer, setPrinter] = useLocal("gloo.printer", {
    name: "",
    paper_width: "80",
    auto_print: false,
    connection: PRINTER_CONNECTION_QZ,
  });

  const [kitchenPrinter, setKitchenPrinter] = useLocal("gloo.kitchen_printer", {
    name: "",
    paper_width: "58",
    auto_print: true,
    connection: PRINTER_CONNECTION_CLEANTER,
    adapter: "cleanter",
    protocol: "escpos",
  });

  const [scanner, setScanner] = useLocal("gloo.scanner", {
    enabled: true,
    suffix: "Enter",
  });

  const [qzConnected, setQzConnected] = useState(false);
  const [qzPrinters, setQzPrinters] = useState([]);
  const [qzBusy, setQzBusy] = useState(false);
  const [cleanterBusy, setCleanterBusy] = useState(false);
  const [kitchenPrinterBusy, setKitchenPrinterBusy] = useState(false);

  const connection = printer.connection || PRINTER_CONNECTION_QZ;
  const isQZMode = connection === PRINTER_CONNECTION_QZ;
  const isCleanterMode = connection === PRINTER_CONNECTION_CLEANTER;
  const isBrowserMode = connection === PRINTER_CONNECTION_BROWSER;

  const loadQZPrinters = async ({ showToast = true } = {}) => {
    if (!isQZMode) {
      return;
    }

    setQzBusy(true);

    try {
      await connectQZ();

      const connected = isQZConnected();
      setQzConnected(connected);

      if (!connected) {
        throw new Error("QZ Tray belum terhubung.");
      }

      const printers = await findQZPrinters();
      const list = Array.isArray(printers)
        ? printers
        : printers
          ? [printers]
          : [];

      setQzPrinters(list);

      if (
        printer.name &&
        list.includes(printer.name)
      ) {
        // Printer tersimpan masih tersedia.
      } else if (list.length === 1 && !printer.name) {
        setPrinter({
          ...printer,
          name: list[0],
        });
      }

      if (showToast) {
        toast.success(
          list.length
            ? `${list.length} printer ditemukan.`
            : "QZ Tray terhubung, tetapi printer tidak ditemukan."
        );
      }
    } catch (error) {
      setQzConnected(false);
      setQzPrinters([]);

      toast.error(
        error?.message ||
          "QZ Tray tidak dapat dihubungkan. Pastikan QZ Tray sudah terinstall dan sedang berjalan."
      );
    } finally {
      setQzBusy(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    if (!isQZMode) {
      setQzConnected(false);
      return () => {
        mounted = false;
      };
    }

    const checkConnection = async () => {
      try {
        if (isQZConnected()) {
          if (mounted) {
            setQzConnected(true);
          }
          return;
        }

        await connectQZ();

        if (mounted) {
          setQzConnected(isQZConnected());
        }
      } catch {
        if (mounted) {
          setQzConnected(false);
        }
      }
    };

    checkConnection();

    return () => {
      mounted = false;
    };
  }, [isQZMode]);

  const handlePrinterChange = (name) => {
    setPrinter({
      ...printer,
      name,
    });
  };

  const handleQZTestPrint = async () => {
    if (!printer.name) {
      toast.error("Pilih printer terlebih dahulu.");
      return;
    }

    setQzBusy(true);

    try {
      const paperWidth = printer.paper_width === "58" ? "58mm" : "80mm";

      await printDocument({
        document: {
          type: "test",
          version: 1,
          data: {
            lines: [
              { align: "center", bold: true, text: "GLOO POS" },
              { align: "center", text: "QZ TEST PRINT" },
              { text: "Printer connection: QZ" },
              { text: `Printer: ${printer.name}` },
              { text: `Paper: ${paperWidth}` },
            ],
          },
        },
        printer: {
          ...printer,
          adapter: "qz",
          connection: "qz_desktop",
        },
        adapter: "qz",
      });

      toast.success("Test print dikirim ke printer.");
    } catch (error) {
      toast.error(
        error?.message ||
          "Test print gagal. Periksa koneksi QZ Tray dan printer."
      );
    } finally {
      setQzBusy(false);
    }
  };

  const handleCleanterTestPrint = async () => {
    if (!printer.name) {
      toast.error("Masukkan nama printer terlebih dahulu.");
      return;
    }

    setCleanterBusy(true);

    try {
      await printDocument({
        document: {
          type: "test",
          version: 1,
          data: {
            lines: [
              { align: "center", bold: true, text: "GLOO POS" },
              { align: "center", text: "CLEANTER TEST PRINT" },
              { text: "Printer connection: Cleanter" },
              { text: `Printer: ${printer.name}` },
              {
                text: `Paper: ${printer.paper_width === "58" ? "58mm" : "80mm"}`,
              },
            ],
          },
        },
        printer: {
          ...printer,
          adapter: "cleanter",
          connection: PRINTER_CONNECTION_CLEANTER,
          protocol: "escpos",
        },
        adapter: "cleanter",
      });

      toast.success("Test print Cleanter dikirim ke printer.");
    } catch (error) {
      toast.error(
        error?.message ||
          "Test print Cleanter gagal. Pastikan aplikasi Cleanter berjalan di Android."
      );
    } finally {
      setCleanterBusy(false);
    }
  };

  const handleKitchenCleanterTestPrint = async () => {
    if (!kitchenPrinter.name?.trim()) {
      toast.error("Isi nama printer dapur terlebih dahulu.");
      return;
    }

    setKitchenPrinter({
      ...kitchenPrinter,
      connection: PRINTER_CONNECTION_CLEANTER,
      adapter: "cleanter",
      protocol: "escpos",
    });

    setKitchenPrinterBusy(true);
    try {
      await printDocument({
        document: {
          type: "kitchen_ticket",
          version: 1,
          data: {
            transaction_number: "TEST-DAPUR",
            batch_sequence: 1,
            customer_name: "Tes Printer Dapur",
            created_at: new Date().toISOString(),
            items: [
              {
                product_id: "test-menu",
                name: "Contoh Menu",
                qty: 1,
                variants: [],
                modifiers: [],
                notes: "Ini hanya tes printer",
              },
            ],
          },
        },
        printer: {
          ...kitchenPrinter,
          connection: PRINTER_CONNECTION_CLEANTER,
          adapter: "cleanter",
          protocol: "escpos",
        },
        adapter: "cleanter",
        copies: 1,
      });
      toast.success("Tiket tes dapur dikirim ke Cleanter.");
    } catch (error) {
      toast.error(
        error?.message || "Tes cetak dapur gagal. Periksa aplikasi Cleanter."
      );
    } finally {
      setKitchenPrinterBusy(false);
    }
  };

  const handleBrowserPrint = () => {
    window.print();
    toast.success("Test print dikirim ke browser/OS print dialog.");
  };

  return (
    <div data-testid="hardware-page" className="max-w-4xl">
      <PageHeader
        title="Hardware"
        subtitle="Device configuration is stored on this terminal only"
        testid="hardware-header"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <h3 className="font-heading font-semibold flex items-center gap-2">
            <Printer size={18} className="text-primary" />
            Receipt Printer
          </h3>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
              Cara terhubung
            </label>

            <select
              data-testid="printer-connection"
              value={connection}
              onChange={(e) => {
                  const nextConnection = e.target.value;

                  const nextAdapter =
                    nextConnection === PRINTER_CONNECTION_QZ
                      ? "qz"
                      : nextConnection === PRINTER_CONNECTION_CLEANTER
                        ? "cleanter"
                        : "browser";

                  const nextProtocol =
                    nextConnection === PRINTER_CONNECTION_CLEANTER ||
                    nextConnection === PRINTER_CONNECTION_QZ
                      ? "escpos"
                      : "none";

                  setPrinter({
                    ...printer,
                    connection: nextConnection,
                    adapter: nextAdapter,
                    protocol: nextProtocol,
                    name: "",
                  });
                }}
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            >
              <option value={PRINTER_CONNECTION_QZ}>
                Desktop — QZ Tray
              </option>
              <option value={PRINTER_CONNECTION_CLEANTER}>
                Android — Cleanter
              </option>
              <option value={PRINTER_CONNECTION_BROWSER}>
                Browser / OS Print
              </option>
            </select>

            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Pilih cara GLOO POS terhubung ke printer thermal.
            </p>
          </div>

          {isQZMode && (
            <div className="rounded-xl border border-border bg-secondary/50 p-3">
              <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                {qzConnected ? (
                  <Wifi size={16} className="text-green-600" />
                ) : (
                  <WifiOff size={16} className="text-muted-foreground" />
                )}
                QZ Tray
              </div>

              <span
                className={
                  qzConnected
                    ? "text-xs font-semibold text-green-600"
                    : "text-xs font-semibold text-muted-foreground"
                }
              >
                {qzConnected ? "Connected" : "Not connected"}
              </span>
            </div>

            <button
              type="button"
              onClick={() => loadQZPrinters()}
              disabled={qzBusy}
              data-testid="qz-refresh-button"
              className="mt-3 w-full h-10 rounded-lg bg-background border border-border font-semibold text-sm hover:bg-accent disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <RefreshCw
                size={15}
                className={qzBusy ? "animate-spin" : ""}
              />
              {qzBusy ? "Connecting..." : "Connect / Refresh Printers"}
            </button>
          </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
              Printer
            </label>

            {isQZMode && qzPrinters.length > 0 ? (
              <select
                data-testid="printer-name"
                value={printer.name}
                onChange={(e) => handlePrinterChange(e.target.value)}
                className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
              >
                <option value="">Select printer</option>
                {qzPrinters.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                data-testid="printer-name"
                value={printer.name}
                onChange={(e) => handlePrinterChange(e.target.value)}
                placeholder="Printer name (e.g. Epson TM-T82)"
                className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
              />
            )}

            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {isQZMode
                ? "Connect QZ Tray untuk mendeteksi printer secara otomatis."
                : isCleanterMode
                  ? "Cleanter menangani koneksi printer Bluetooth di Android."
                  : "Browser / OS Print digunakan untuk mencetak melalui dialog sistem."}
            </p>
          </div>

          <select
            data-testid="printer-paper-width"
            value={printer.paper_width}
            onChange={(e) =>
              setPrinter({
                ...printer,
                paper_width: e.target.value,
              })
            }
            className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
          >
            <option value="58">58mm thermal</option>
            <option value="80">80mm thermal</option>
          </select>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={printer.auto_print}
              onChange={(e) =>
                setPrinter({
                  ...printer,
                  auto_print: e.target.checked,
                })
              }
              data-testid="printer-auto-print"
            />
            Auto-print after payment
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {isQZMode && (
              <button
                type="button"
                onClick={handleQZTestPrint}
                disabled={qzBusy || !qzConnected || !printer.name}
                data-testid="printer-test-qz-button"
                className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:opacity-90 disabled:opacity-50"
              >
                {qzBusy ? "Printing..." : "Test Print QZ"}
              </button>
            )}

            {isCleanterMode && (
              <button
                type="button"
                onClick={handleCleanterTestPrint}
                disabled={cleanterBusy || !printer.name}
                data-testid="printer-test-cleanter-button"
                className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:opacity-90 disabled:opacity-50"
              >
                {cleanterBusy ? "Printing..." : "Test Print Cleanter"}
              </button>
            )}

            {isBrowserMode && (
              <button
                type="button"
                onClick={handleBrowserPrint}
                data-testid="printer-test-button"
                className="w-full h-11 rounded-xl bg-secondary border border-border font-bold text-sm hover:bg-accent"
              >
                Browser Print
              </button>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground">
            QZ Tray digunakan untuk direct printing ke printer thermal.
            Browser Print tetap tersedia sebagai fallback.
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <h3 className="font-heading font-semibold flex items-center gap-2">
            <Printer size={18} className="text-primary" />
            Kitchen Printer
          </h3>

          <p className="text-xs text-muted-foreground">
            Konfigurasi terpisah dari Receipt Printer. Koneksi dapur menggunakan Cleanter di Android.
          </p>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
              Nama printer dapur
            </label>
            <input
              data-testid="kitchen-printer-name"
              value={kitchenPrinter.name}
              onChange={(e) =>
                setKitchenPrinter({ ...kitchenPrinter, name: e.target.value })
              }
              placeholder="Contoh: Xprinter XP-58IIZ"
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
              Ukuran kertas dapur
            </label>
            <select
              data-testid="kitchen-printer-paper-width"
              value={kitchenPrinter.paper_width}
              onChange={(e) =>
                setKitchenPrinter({ ...kitchenPrinter, paper_width: e.target.value })
              }
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            >
              <option value="58">58mm thermal</option>
              <option value="80">80mm thermal</option>
            </select>
          </div>

          <button
            type="button"
            onClick={handleKitchenCleanterTestPrint}
            disabled={kitchenPrinterBusy || !kitchenPrinter.name?.trim()}
            data-testid="kitchen-printer-test-button"
            className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:opacity-90 disabled:opacity-50"
          >
            {kitchenPrinterBusy ? "Mengirim tiket tes..." : "Tes Cetak Tiket Dapur"}
          </button>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <h3 className="font-heading font-semibold flex items-center gap-2">
            <ScanBarcode size={18} className="text-primary" />
            Barcode Scanner
          </h3>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={scanner.enabled}
              onChange={(e) =>
                setScanner({
                  ...scanner,
                  enabled: e.target.checked,
                })
              }
              data-testid="scanner-enabled"
            />
            Keyboard-wedge scanner enabled
          </label>

          <select
            data-testid="scanner-suffix"
            value={scanner.suffix}
            onChange={(e) =>
              setScanner({
                ...scanner,
                suffix: e.target.value,
              })
            }
            className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
          >
            <option value="Enter">Enter suffix</option>
            <option value="Tab">Tab suffix</option>
          </select>

          <p className="text-[11px] text-muted-foreground">
            On the POS screen, rapid key input ending with Enter is treated
            as a barcode scan and adds the matching product to the cart
            instantly — no modal required.
          </p>
        </div>
      </div>
    </div>
  );
}
